import { useMutation, useQueryClient } from "react-query";
import { ApiConversation, ApiFragmentNode, ApiMessageTypes } from "../shared/interfaces";
import { ConversationApi } from "../api/conversation";
import useSendMessageMutation from "./useSendMessageMutation";
import { useNavigate } from "react-router-dom";
import { PrimeQueryKeys } from "../shared/constants";
import { AnalyticsEvents, analyticsEvent } from "../util/analytics";

interface CreateConversationAndNavigateArgs {
    message: string;
    messageType: string;
    fragmentNode?: ApiFragmentNode;
}

export default function useCreateConversationAndNavigate(): (
    message: string,
    messageType: string,
    fragmentNode?: ApiFragmentNode,
) => void {
    const queryClient = useQueryClient();
    const sendMessageMutation = useSendMessageMutation();

    const navigate = useNavigate();

    const mutation = useMutation<ApiConversation, unknown, CreateConversationAndNavigateArgs>({
        mutationKey: PrimeQueryKeys.createConversation,
        mutationFn: async ({ message, messageType, fragmentNode }) => {
            if (messageType === ApiMessageTypes.Markdown) {
                analyticsEvent(AnalyticsEvents.CREATE_CONVERSATION, {
                    node: !!fragmentNode,
                });
            } else if (messageType === ApiMessageTypes.GenerateSpreadsheet) {
                analyticsEvent(AnalyticsEvents.REQUEST_GENERATE_SPREADSHEET);
            }

            return await ConversationApi.createConversation(message, messageType);
        },
        onSuccess: (result, { message, messageType, fragmentNode }) => {
            queryClient.setQueryData("conversations", (old: ApiConversation[] = []) => [
                ...old,
                result,
            ]);

            navigate(`/c/${result.id}`);

            sendMessageMutation.mutate({
                conversationId: result.id,
                files: [],
                message,
                messageType,
                fragmentNode,
            });
        },
    });

    return (message: string, messageType: string, fragmentNode?: ApiFragmentNode) =>
        mutation.mutate({ message, messageType, fragmentNode });
}
